import React, { FunctionComponent, useEffect, useState } from 'react'
import { useCustomerNotificationExclusions } from 'CustomerNotificationExclusions/CustomerNotificationExclusionsProvider'
import { useTranslation } from 'react-i18next'
import { useMessage } from 'Elements/Message'
import { ICustomerNotificationExclusionsListExclusionData, useApi } from 'components/providers/ApiProvider'
import ListSkeleton from 'Elements/ListSkeleton'
import ListLoadingIndicator from 'Elements/ListLoadingIndicator'
import ListNoRecords from 'Elements/ListNoRecords'
import TableContainer from '@material-ui/core/TableContainer'
import Table from '@material-ui/core/Table'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import TableBody from '@material-ui/core/TableBody'
import ListTablePagination from 'Elements/ListTablePagination'
import ListTableRow from 'Elements/ListTableRow'
import { formatTimestamp } from 'utils'
import Tooltip from '@material-ui/core/Tooltip'
import IconInfo from '@material-ui/icons/InfoOutlined'
import IconButton from '@material-ui/core/IconButton'
import IconDelete from '@material-ui/icons/Delete'

export type Props = {}
export type Functions = {}
export type AllProps = Props & Functions

const List: FunctionComponent<AllProps> = ({}) => {
  const { t } = useTranslation()
  const api = useApi()
  const { filter, loadList, listLoading, setListLoading, listPerPage, onChangePerPage, listPage, setListPage, onChangePage } = useCustomerNotificationExclusions()
  const { showSuccess, showError } = useMessage()

  const [exclusions, setExclusions] = useState<ICustomerNotificationExclusionsListExclusionData[]>(null)
  const [totalExclusions, setTotalExclusions] = useState(0)

  const load = (onLoad?: Function, page?: number) => {
    if (!page) {
      setListLoading(true)
    }

    api.getCustomerNotificationExclusionsList({
      mobilly_client_id: filter.mobillyClientId || undefined,
      plate_number: filter.plateNumber || undefined,
      limit: listPerPage,
      page: page || listPage,
    })
      .then(data => {
        if (!page && data.exclusions && !data.exclusions.length && data.totalExclusions) {
          const pages = Math.ceil(data.totalExclusions / listPerPage)

          if (listPage > pages) {
            setListPage(pages)
            load(onLoad, pages)

            return
          }
        }

        setListLoading(false)

        if (onLoad) {
          onLoad(data.exclusions)
        }

        setExclusions(data.exclusions)
        setTotalExclusions(data.totalExclusions)
      })
      .catch(error => {
        setListLoading(false)

        showError(`${t('alerts.couldNotLoadData')} ${t('alerts.tryLater')}`)
      })
  }

  useEffect(() => {
    load()
  }, [loadList])

  const handleDelete = (exclusion: ICustomerNotificationExclusionsListExclusionData) => {
    if (!confirm(t('confirms.deleteExclusion'))) {
      return
    }

    api.deleteCustomerNotificationExclusion({
      exclusion_id: exclusion.id,
    })
      .then(data => {
        if (!data || !data.status) {
          throw new Error
        }

        load()

        showSuccess(t('messages.exclusionDeleted'))
      })
      .catch(error => {
        const message = t('alerts.tryLater')

        showError(`${t('alerts.couldNotDeleteExclusion')} ${message}`)
      })
  }

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    onChangePage(page + 1)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChangePerPage(parseInt(event.target.value, 10))
  }

  return (
    <>
      {!exclusions && (
        <ListSkeleton />
      )}

      {exclusions && listLoading && (
        <ListLoadingIndicator />
      )}

      {exclusions && !exclusions.length && (
        <ListNoRecords />
      )}

      {exclusions && Boolean(exclusions.length) && (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('mobillyClientId')}</TableCell>
                  <TableCell>{t('plateNumber')}</TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableHead>
              <TableBody>
                {exclusions.map((exclusion, key) => (
                  <ListTableRow key={key}>
                    <TableCell>
                      {exclusion.mobilly_client_id}
                    </TableCell>
                    {exclusion.plate_number === '*' ? (
                      <TableCell style={{ fontStyle: 'italic' }}>
                        {t('allPlateNumbers')}
                      </TableCell>
                    ) : (
                      <TableCell style={{ fontFamily: '"Roboto Mono", monospace' }}>
                        {exclusion.plate_number}
                      </TableCell>
                    )}
                    <TableCell align="center" style={{ width: 62 }}>
                      <Tooltip title={`@ ${formatTimestamp(exclusion.create_time)}`} placement={'top'} arrow interactive>
                        <IconInfo fontSize="small" style={{ verticalAlign: 'middle' }} />
                      </Tooltip>
                    </TableCell>
                    <TableCell style={{ paddingLeft: 0, width: 46 }}>
                      <IconButton
                        color="secondary"
                        size="small"
                        style={{ margin: '-5px 0px' }}
                        onClick={() => handleDelete(exclusion)}
                      >
                        <IconDelete />
                      </IconButton>
                    </TableCell>
                  </ListTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <ListTablePagination
            count={totalExclusions}
            rowsPerPage={listPerPage}
            page={listPage - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </>
  )
}

export default List
